<template>
  <div class="store-report">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <h2>📊 单店报告成绩</h2>
        <el-select v-model="formKey" placeholder="请选择问卷" size="small" style="width: 250px; margin-left: 16px" @change="onFormChange">
          <el-option v-for="form in formList" :key="getFormKey(form)" :label="stripHtml(form.name)" :value="getFormKey(form)" />
        </el-select>
      </div>
      <div class="header-right">
        <el-input 
          v-model="filterKeyword" 
          placeholder="搜索区域/门店名称" 
          prefix-icon="el-icon-search" 
          size="small" 
          style="width: 200px; margin-right: 10px"
          clearable
          @input="handleFilter"
        />
        <el-button size="small" type="primary" icon="el-icon-s-data" :loading="generating" @click="generateReport">生成/更新统计</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </div>
    
    <!-- 快照信息 -->
    <div class="snapshot-info" v-if="snapshotInfo">
      <i class="el-icon-time"></i>
      数据更新时间: {{ snapshotInfo.generateTime | formatTime }}
      <span style="margin-left: 15px">统计门店数: {{ snapshotInfo.storeCount }}</span>
    </div>

    <!-- 统计概览 -->
    <div class="stats-bar" v-if="nationalData">
      <div class="stat-card">
        <div class="stat-value">{{ nationalData.avgScore || 0 }}</div>
        <div class="stat-label">全国平均分</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ nationalData.storeCount || 0 }}</div>
        <div class="stat-label">已审核门店数</div>
      </div>
    </div>

    <!-- 统一数据表格 -->
    <div class="report-content" v-if="formKey">
      <el-table 
        :data="displayData" 
        stripe 
        v-loading="loading" 
        row-key="uniqueId"
        border
        class="custom-table"
        header-row-class-name="table-header"
        @expand-change="handleExpandChange"
      >
        <!-- 展开列：显示环节详情 -->
        <el-table-column type="expand" width="50">
          <template slot-scope="{ row }">
            <div class="expand-wrapper" v-loading="row.detailLoading">
              <!-- 详情标题 -->
              <div class="detail-header">
                <span class="detail-title">
                  <i class="el-icon-s-data"></i> 
                  {{ row.name }} - 环节得分详情
                  <span class="detail-tag" v-if="row.rowType === 'store'"> (总分: {{ row.avgScore }})</span>
                  <span class="detail-tag" v-else> (平均分: {{ row.avgScore }})</span>
                </span>
                <span class="detail-subtitle" v-if="row.rowType === 'store'">
                  排位情况: 
                  在小区 #{{ row.rankInXiaoqu || '-' }} / 
                  在大区 #{{ row.xiaoquRankInDaqu || '-' }} / 
                  在全国 #{{ row.daquRankInNational || '-' }}
                </span>
              </div>
              
              <!-- 算法说明 -->
              <div class="algorithm-note">
                <i class="el-icon-info"></i>
                <span v-if="row.rowType === 'national'">
                  <strong>全国汇总：</strong>各环节得分 = 所有门店该环节得分的<strong>平均值</strong>
                </span>
                <span v-else-if="row.rowType === 'daqu'">
                  <strong>战区汇总：</strong>各环节得分 = 本战区所有门店该环节得分的<strong>平均值</strong>
                </span>
                <span v-else-if="row.rowType === 'xiaoqu'">
                  <strong>小区汇总：</strong>各环节得分 = 本小区所有门店该环节得分的<strong>平均值</strong>
                </span>
                <span v-else>
                  <strong>门店明细：</strong>各环节得分 = 该门店各题目得分的<strong>实际汇总</strong>
                </span>
              </div>
              
              <!-- 环节得分表格 -->
              <div class="segment-table-container">
                <table class="segment-table">
                  <thead>
                    <tr>
                      <th style="width: 40%">环节名称</th>
                      <th style="width: 15%" class="text-center">标准分</th>
                      <th style="width: 15%" class="text-center">实际得分</th>
                      <th style="width: 15%" class="text-center">得分率</th>
                      <th style="width: 15%" class="text-center">状态</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="seg in row.segmentTree">
                      <tr :key="seg.segmentKey" class="level-1" @click="toggleSegmentExpand(seg)">
                        <td class="col-name">
                          <span class="expand-icon">{{ seg.expanded === false ? '[+]' : '[-]' }}</span>
                          <span class="icon">📂</span>
                          {{ seg.segmentName }}
                        </td>
                        <td class="text-center">{{ seg.targetScore }}</td>
                        <td class="text-center">{{ seg.actualScore }}</td>
                        <td class="text-center">{{ seg.scoreRate }}%</td>
                        <td class="text-center">{{ getStatusIcon(seg.scoreRate) }}</td>
                      </tr>
                      <template v-if="seg.expanded !== false && seg.children">
                        <template v-for="child in seg.children">
                          <tr :key="child.segmentKey" class="level-2" @click="toggleSegmentExpand(child)">
                            <td class="col-name">
                              <span class="indent-2"></span>
                              <span v-if="(child.children && child.children.length) || (child.items && child.items.length)" class="expand-icon">{{ child.expanded === false ? '[+]' : '[-]' }}</span>
                              <span class="icon">📁</span>
                              {{ child.segmentName }}
                              <span v-if="child.itemCount" class="item-count">({{ child.itemCount }}题)</span>
                            </td>
                            <td class="text-center">{{ child.targetScore }}</td>
                            <td class="text-center">{{ child.actualScore }}</td>
                            <td class="text-center">{{ child.scoreRate }}%</td>
                            <td class="text-center">{{ getStatusIcon(child.scoreRate) }}</td>
                          </tr>
                          <!-- 如果二级有三级children，渲染三级 -->
                          <template v-if="child.expanded !== false && child.children && child.children.length">
                            <template v-for="leaf in child.children">
                              <tr :key="leaf.segmentKey" class="level-3" @click="toggleSegmentExpand(leaf)">
                                <td class="col-name">
                                  <span class="indent-3"></span>
                                  <span v-if="leaf.items && leaf.items.length" class="expand-icon">{{ leaf.expanded === false ? '[+]' : '[-]' }}</span>
                                  <span class="icon">📄</span>
                                  {{ leaf.segmentName }}
                                  <span v-if="leaf.itemCount" class="item-count">({{ leaf.itemCount }}题)</span>
                                </td>
                                <td class="text-center">{{ leaf.targetScore }}</td>
                                <td class="text-center">{{ leaf.actualScore }}</td>
                                <td class="text-center">{{ leaf.scoreRate }}%</td>
                                <td class="text-center">{{ getStatusIcon(leaf.scoreRate) }}</td>
                              </tr>
                              <!-- 三级的题目列表 -->
                              <template v-if="leaf.expanded !== false && leaf.items && leaf.items.length">
                                <tr v-for="item in leaf.items" :key="item.itemKey" class="level-item">
                                  <td class="col-name item-row">
                                    <span class="indent-4"></span>
                                    <span class="icon">📝</span>
                                    <span class="item-label" v-html="stripHtml(item.label)"></span>
                                  </td>
                                  <td class="text-center item-row">{{ item.targetScore || '-' }}</td>
                                  <td class="text-center item-row">{{ item.actualScore }}</td>
                                  <td class="text-center item-row">{{ item.scoreRate }}%</td>
                                  <td class="text-center item-row">{{ getStatusIcon(item.scoreRate) }}</td>
                                </tr>
                              </template>
                            </template>
                          </template>
                          <!-- 如果二级没有三级children，直接显示二级的items -->
                          <template v-if="child.expanded !== false && (!child.children || child.children.length === 0) && child.items && child.items.length">
                            <tr v-for="item in child.items" :key="item.itemKey" class="level-item">
                              <td class="col-name item-row">
                                <span class="indent-3"></span>
                                <span class="icon">📝</span>
                                <span class="item-label" v-html="stripHtml(item.label)"></span>
                              </td>
                              <td class="text-center item-row">{{ item.targetScore || '-' }}</td>
                              <td class="text-center item-row">{{ item.actualScore }}</td>
                              <td class="text-center item-row">{{ item.scoreRate }}%</td>
                              <td class="text-center item-row">{{ getStatusIcon(item.scoreRate) }}</td>
                            </tr>
                          </template>
                        </template>
                      </template>
                    </template>
                    <tr v-if="!row.segmentTree || row.segmentTree.length === 0">
                      <td colspan="5" class="text-center text-muted" style="padding: 20px;">暂无详情数据</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="类型" width="80" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="row.rowType === 'national'" size="mini" type="danger">全国</el-tag>
            <el-tag v-else-if="row.rowType === 'daqu'" size="mini" type="warning">战区</el-tag>
            <el-tag v-else-if="row.rowType === 'xiaoqu'" size="mini" type="success">小区</el-tag>
            <el-tag v-else size="mini" type="info">门店</el-tag>
          </template>
        </el-table-column>
        
        <el-table-column label="名称" min-width="200">
          <template slot-scope="{ row }">
            <span :class="'row-type-' + row.rowType" class="name-cell">
              <span v-if="row.rowType === 'national'" class="type-icon">🌏</span>
              <span v-else-if="row.rowType === 'daqu'" class="type-icon">🏢</span>
              <span v-else-if="row.rowType === 'xiaoqu'" class="type-icon">🏠</span>
              <span v-else class="type-icon">🏪</span>
              
              <span class="name-text">{{ row.name }}</span>
              <el-tag v-if="row.rowType === 'store' && row.code" size="mini" type="info" style="margin-left: 8px">{{ row.code }}</el-tag>
            </span>
          </template>
        </el-table-column>
        
        <el-table-column label="门店数" prop="storeCount" width="100" align="center">
          <template slot-scope="{ row }">
            <span v-if="row.rowType !== 'store'">{{ row.storeCount }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        
        <el-table-column label="总体得分" width="140" align="center">
          <template slot="header">
            总体得分
            <el-tooltip content="全国/战区/小区显示的是下级数据的平均分" placement="top">
              <i class="el-icon-info" style="color: #909399; cursor: help; margin-left: 4px;"></i>
            </el-tooltip>
          </template>
          <template slot-scope="{ row }">
            <span :class="getScoreClass(row.avgScore)" class="score-text">
              {{ row.avgScore }}
              <span v-if="row.rowType !== 'store'" style="font-size: 11px; color: #909399;"> (均值)</span>
            </span>
          </template>
        </el-table-column>
        
        <el-table-column label="全国排名" width="100" align="center">
          <template slot-scope="{ row }">
             <!-- 门店显示全国排名，区域显示区域排名 -->
             <span v-if="row.rowType === 'store'">{{ row.daquRankInNational || '-' }}</span>
             <span v-else-if="row.rowType === 'daqu'">{{ row.rank }}/{{ row.rankTotal }}</span>
             <span v-else>-</span>
          </template>
        </el-table-column>
        
        <el-table-column label="战区排名" width="100" align="center">
          <template slot-scope="{ row }">
             <span v-if="row.rowType === 'store'">{{ row.xiaoquRankInDaqu || '-' }}</span>
             <span v-else-if="row.rowType === 'xiaoqu'">{{ row.rank }}/{{ row.rankTotal }}</span>
             <span v-else>-</span>
          </template>
        </el-table-column>
        
        <!-- 一级指标列 (动态) -->
        <el-table-column v-for="col in topLevelSegments" :key="col.segmentKey" :label="col.segmentName" width="120" align="center">
           <template slot-scope="{ row }">
             {{ getSegmentScore(row, col.segmentKey) }}
           </template>
        </el-table-column>
        
      </el-table>
    </div>
    
    <div class="empty-tip" v-else>
      <i class="el-icon-document-copy"></i>
      <p>请选择一个问卷查看报告</p>
    </div>
  </div>
</template>

<script>
import axios from 'index/axios'
import { getProjectForms } from '@/app/index/api/project/manager'

export default {
  name: 'StoreReport',
  data() {
    return {
      projectId: this.$route.params.projectId,
      formKey: '',
      formList: [],
      loading: false,
      generating: false,
      filterKeyword: '',
      
      // 原始数据
      rawData: [], // 树形结构
      displayData: [], // 经过筛选的树
      
      nationalData: null,
      topLevelSegments: [],
      snapshotInfo: null // 快照信息
    }
  },
  filters: {
    formatTime(val) {
      if (!val) return '-'
      const d = new Date(val)
      return d.toLocaleString('zh-CN')
    }
  },
  created() {
    this.loadFormList()
  },
  methods: {
    async loadFormList() {
      try {
        const res = await getProjectForms(this.projectId)
        this.formList = res.data?.data || res.data || []
        // 只保留考试类问卷或所有问卷？假设所有
        if (this.formList.length > 0) {
          this.formKey = this.getFormKey(this.formList[0])
          this.loadData()
        }
      } catch (e) {
        console.error('[StoreReport] loadFormList error:', e)
      }
    },
    
    getFormKey(form) {
      return form.formKey || form.key || form.sourceId || form.source_id || ''
    },
    
    stripHtml(html) {
      if (!html) return ''
      return html.replace(/<[^>]*>/g, '')
    },
    
    onFormChange() {
      this.loadData()
    },
    
    async loadData() {
      if (!this.formKey) return
      this.loading = true
      try {
        // Step 1: 尝试从快照加载
        const snapshotRes = await axios.post('/Report/snapshot', {
          ProjectId: this.projectId,
          FormKey: this.formKey
        })
        const snapshotWrapper = snapshotRes.data || snapshotRes
        const snapshotData = snapshotWrapper.data || snapshotWrapper
        
        if (snapshotData && snapshotData.hasSnapshot && snapshotData.data) {
          // 使用快照数据
          this.snapshotInfo = {
            generateTime: snapshotData.generateTime,
            storeCount: snapshotData.storeCount
          }
          this.processRegionalData(snapshotData.data)
          return
        }
        
        // Step 2: 无快照，实时计算
        this.snapshotInfo = null
        const res = await axios.post('/Report/regional', {
          ProjectId: this.projectId,
          FormKey: this.formKey
        })
        const wrapper = res.data || res
        const data = wrapper.data || wrapper
        
        if (data && !data.errcode) {
          this.processRegionalData(data)
        }
      } catch (e) {
        console.error('[StoreReport] Load data error:', e)
        this.$message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },
    
    processRegionalData(data) {
      // 兼容 PascalCase 和 camelCase
      const national = data.national || data.National
      const daquRows = data.daquRows || data.DaquRows || []
      const segmentColumns = data.segmentColumns || data.SegmentColumns || []
      
      this.nationalData = national
      this.topLevelSegments = segmentColumns.filter(s => stringIsNullOrEmpty(s.parentKey || s.ParentKey))
      
      if (!this.nationalData) {
         this.rawData = []
         this.displayData = []
         return
      }
      
      // 辅助函数：移除 children 属性
      const stripChildren = (obj) => {
        const { children, Children, ...rest } = obj
        return rest
      }
      
      // 构建平铺数据 (Flat List) - 全部平行展示
      const flatList = []
      
      // 1. 添加全国
      flatList.push({
        ...stripChildren(this.nationalData),
        uniqueId: 'national_0',
        rowType: 'national',
        name: this.nationalData.name || this.nationalData.Name || '全国',
        avgScore: this.nationalData.avgScore || this.nationalData.AvgScore || 0,
        storeCount: this.nationalData.storeCount || this.nationalData.StoreCount || 0
      })
      
      // 2. 添加所有战区(大区)
      daquRows.forEach(daqu => {
        flatList.push({
          ...stripChildren(daqu),
          uniqueId: `daqu_${daqu.id || daqu.Id}`,
          rowType: 'daqu',
          name: daqu.name || daqu.Name,
          avgScore: daqu.avgScore || daqu.AvgScore,
          storeCount: daqu.storeCount || daqu.StoreCount,
          rank: daqu.rank || daqu.Rank,
          rankTotal: daqu.rankTotal || daqu.RankTotal
        })
      })
      
      // 3. 添加所有小区
      daquRows.forEach(daqu => {
        const children = daqu.children || daqu.Children || []
        children.forEach(xiaoqu => {
          flatList.push({
            ...stripChildren(xiaoqu),
            uniqueId: `xiaoqu_${xiaoqu.id || xiaoqu.Id}`,
            rowType: 'xiaoqu',
            name: xiaoqu.name || xiaoqu.Name,
            avgScore: xiaoqu.avgScore || xiaoqu.AvgScore,
            storeCount: xiaoqu.storeCount || xiaoqu.StoreCount,
            rank: xiaoqu.rank || xiaoqu.Rank,
            rankTotal: xiaoqu.rankTotal || xiaoqu.RankTotal,
            parentDaquName: daqu.name || daqu.Name
          })
        })
      })
      
      // 4. 添加所有门店
      daquRows.forEach(daqu => {
        const daquChildren = daqu.children || daqu.Children || []
        daquChildren.forEach(xiaoqu => {
          const xiaoquChildren = xiaoqu.children || xiaoqu.Children || []
          xiaoquChildren.forEach(store => {
            flatList.push({
              ...stripChildren(store),
              uniqueId: `store_${store.id || store.Id}`,
              code: store.storeCode || store.StoreCode || store.code || store.Code,
              rowType: 'store',
              name: store.name || store.Name,
              avgScore: store.avgScore || store.AvgScore,
              parentXiaoquName: xiaoqu.name || xiaoqu.Name,
              parentDaquName: daqu.name || daqu.Name,
              daquRankInNational: store.daquRankInNational || store.DaquRankInNational,
              xiaoquRankInDaqu: store.xiaoquRankInDaqu || store.XiaoquRankInDaqu,
              rankInXiaoqu: store.rankInXiaoqu || store.RankInXiaoqu || store.rank || store.Rank
            })
          })
        })
      })
      
      console.log('[StoreReport] Flat list:', flatList)
      this.rawData = flatList
      this.handleFilter()
    },
    
    async generateReport() {
      if (!this.formKey) {
        this.$message.warning('请先选择问卷')
        return
      }
      this.generating = true
      try {
        const res = await axios.post('/Report/generate', {
          ProjectId: this.projectId,
          FormKey: this.formKey
        })
        const data = res.data?.data || res.data
        if (data && !data.errcode) {
          this.$message.success(`统计生成成功，共 ${data.storeCount || 0} 家门店`)
          // 重新加载数据
          await this.loadData()
        } else {
          this.$message.error(data?.message || '生成失败')
        }
      } catch (e) {
        console.error(e)
        this.$message.error('生成失败')
      } finally {
        this.generating = false
      }
    },
    
    handleFilter() {
      if (!this.filterKeyword) {
        this.displayData = [...this.rawData]
        return
      }
      
      const keyword = this.filterKeyword.toLowerCase()
      
      // 平铺数据的简单过滤
      this.displayData = this.rawData.filter(row => {
        const nameMatch = row.name && row.name.toLowerCase().includes(keyword)
        const codeMatch = row.code && row.code.toLowerCase().includes(keyword)
        const parentMatch = (row.parentDaquName && row.parentDaquName.toLowerCase().includes(keyword)) ||
                           (row.parentXiaoquName && row.parentXiaoquName.toLowerCase().includes(keyword))
        return nameMatch || codeMatch || parentMatch
      })
    },
    
    async handleExpandChange(row, expandedRows) {
      if (expandedRows.find(r => r.uniqueId === row.uniqueId) && !row.segmentTree) {
        this.$set(row, 'detailLoading', true)
        try {
          if (row.rowType === 'store') {
            // 紧急修复策略：对于 store 类型，我们调用 Report/store/list 搜索该门店获取 answerKey
            const listRes = await axios.post('/Report/store/list', {
                ProjectId: this.projectId,
                FormKey: this.formKey,
               StoreName: row.name
            })
            const listData = listRes.data?.data || listRes.data
            if (listData && listData.records && listData.records.length > 0) {
                 const target = listData.records[0] // 近似匹配
                 // 调用详情
                 const detailRes = await axios.post('/Report/store/detail', {
                    ProjectId: this.projectId,
                    FormKey: this.formKey,
                    AnswerKey: target.answerKey,
                    StoreCode: target.storeCode
                 })
                 const detailData = detailRes.data?.data || detailRes.data
                 this.processSegmentData(row, detailData.segments)
                 // 补充排名信息
                 this.$set(row, 'rankInXiaoqu', target.rankInXiaoqu)
                 this.$set(row, 'xiaoquRankInDaqu', target.xiaoquRankInDaqu)
                 this.$set(row, 'daquRankInNational', target.daquRankInNational)
            }
          } else {
            // 区域/全国聚合详情
            const detailRes = await axios.post('/Report/region/detail', {
                ProjectId: this.projectId,
                FormKey: this.formKey,
                Level: row.rowType === 'national' ? 0 : (row.rowType === 'daqu' ? 2 : 3),
                Id: row.id // DaquId or XiaoquId
            })
            const detailData = detailRes.data?.data || detailRes.data
            this.processSegmentData(row, detailData.segments)
          }
        } catch (e) {
          console.error(e)
          this.$message.error('加载详情失败')
        } finally {
          this.$set(row, 'detailLoading', false)
        }
      }
    },
    
    processSegmentData(row, segments) {
      if (!segments) return
      
      // 递归设置所有节点为展开状态（包括有items的叶子节点）
      // 兼容 PascalCase 和 camelCase
      const initExpand = (segs) => {
        return segs.map(seg => {
          const items = seg.items || seg.Items || []
          const children = seg.children || seg.Children || []
          return {
            ...seg,
            expanded: true, // 默认全部展开
            children: children.length ? initExpand(children) : [],
            items: items.map(item => ({
              itemKey: item.itemKey || item.ItemKey,
              label: item.label || item.Label,
              targetScore: item.targetScore || item.TargetScore || 0,
              actualScore: item.actualScore || item.ActualScore || 0,
              scoreRate: item.scoreRate || item.ScoreRate || 0
            }))
          }
        })
      }
      this.$set(row, 'segmentTree', initExpand(segments))
      console.log('[StoreReport] segmentTree:', row.segmentTree)
    },
    
    toggleSegmentExpand(seg) {
      this.$set(seg, 'expanded', seg.expanded === false ? true : false)
    },
    
    getSegmentScore(row, segKey) {
       // 顶层分数从 SegmentScores 字典拿 (RegionalRow)
       if (row.segmentScores && row.segmentScores[segKey] !== undefined) {
           return row.segmentScores[segKey]
       }
       // 或者从 summary (StoreListItem)
       if (row.segmentScores && Array.isArray(row.segmentScores)) {
           const match = row.segmentScores.find(s => s.segmentKey === segKey)
           return match ? match.score : '-'
       }
       // 从 segmentTree 中查找 (如果已加载)
       if (row.segmentTree) {
           const match = row.segmentTree.find(s => s.segmentKey === segKey)
           if (match) return match.actualScore
       }
       return '-'
    },
    
    getStatusIcon(rate) {
      if (rate >= 90) return '✅'
      if (rate >= 80) return '✅'
      if (rate >= 60) return '⚠️'
      return '❌'
    },
    
    getScoreClass(score) {
      if (!score) return ''
      const num = parseFloat(score)
      if (num >= 90) return 'text-success'
      if (num >= 80) return 'text-primary'
      if (num >= 60) return 'text-warning'
      return 'text-danger'
    }
  }
}

function stringIsNullOrEmpty(str) {
    return !str || str === ''
}
</script>

<style scoped>
.store-report {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
  margin-bottom: 20px;
}
.header-left {
  display: flex;
  align-items: center;
}
.header-left h2 {
  margin: 0;
  font-size: 18px;
  color: #303133;
}
.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.snapshot-info {
  background: #ecf5ff;
  padding: 10px 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  color: #409EFF;
  font-size: 13px;
}

.stats-bar {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}
.stat-card {
  background: #fff;
  padding: 20px 30px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 150px;
  flex: 1;
}
.stat-value {
  font-size: 32px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 8px;
}
.stat-label {
  color: #909399;
  font-size: 14px;
}

.report-content {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
}

/* 表格样式自定义 */
/* 保持简约风格，移除自定义表头颜色 */
.custom-table ::v-deep .table-header th {
  font-weight: 500;
  border-right: 1px solid #ebeef5;
}
.custom-table ::v-deep .table-header th:last-child {
  border-right: none;
}

.name-cell {
  display: flex;
  align-items: center;
}
.type-icon {
  margin-right: 8px;
  font-size: 16px;
}
.name-text {
  font-weight: 500;
}
.row-type-national .name-text { color: #303133; font-weight: bold; font-size: 15px; }
.row-type-daqu .name-text { color: #409EFF; }
.row-type-xiaoqu .name-text { color: #67C23A; }
.row-type-store .name-text { color: #606266; font-weight: normal; }

.score-text {
  font-weight: bold;
}
.text-success { color: #67C23A; }
.text-primary { color: #409EFF; }
.text-warning { color: #E6A23C; }
.text-danger { color: #F56C6C; }

/* 详情展开区域 */
.expand-wrapper {
  padding: 20px;
  background: #f8fbfd;
  border-top: 1px solid #ebeef5;
}
.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}
.detail-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
  display: flex;
  align-items: center;
}
.detail-title i { margin-right: 8px; color: #409EFF; }
.detail-tag { font-size: 14px; color: #909399; margin-left: 10px; font-weight: normal; }
.detail-subtitle {
  font-size: 13px;
  color: #606266;
  background: #eef1f6;
  padding: 4px 10px;
  border-radius: 4px;
}

.algorithm-note {
  background: #fdf6ec;
  border: 1px solid #faecd8;
  border-radius: 4px;
  padding: 10px 15px;
  margin-bottom: 15px;
  font-size: 13px;
  color: #e6a23c;
}
.algorithm-note i {
  margin-right: 8px;
}
.algorithm-note strong {
  color: #b88230;
}

.segment-table-container {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.segment-table {
  width: 100%;
  border-collapse: collapse;
}
.segment-table th {
  background: #f5f7fa;
  padding: 10px 15px;
  color: #606266;
  font-weight: 600;
  border-bottom: 1px solid #ebeef5;
  text-align: left;
}
.segment-table th.text-center { text-align: center; }
.segment-table td {
  padding: 10px 15px;
  border-bottom: 1px solid #ebeef5;
  color: #606266;
}
.segment-table tr:last-child td { border-bottom: none; }

.level-1 { cursor: pointer; background: #fff; font-weight: 500; }
.level-1:hover { background: #f0f9eb; }
.level-2 { cursor: pointer; }
.level-2:hover { background: #f9fafc; }
.level-3 { color: #909399; cursor: pointer; }
.level-3:hover { background: #fafbfc; }
.level-item { background: #fafafa; }

.col-name { position: relative; }
.expand-icon {
  display: inline-block;
  width: 20px;
  text-align: center;
  color: #C0C4CC;
  margin-right: 4px;
}
.icon { margin-right: 5px; opacity: 0.8; }
.indent-2 { display: inline-block; width: 20px; margin-left: 20px; }
.indent-3 { display: inline-block; width: 20px; margin-left: 45px; }
.indent-4 { display: inline-block; width: 20px; margin-left: 70px; }

.item-row { 
  font-size: 12px;
  color: #909399;
  background: #fafafa;
}
.item-label {
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  vertical-align: middle;
}

.text-center { text-align: center; }
.empty-tip {
  text-align: center;
  padding: 100px 0;
  color: #909399;
}
.empty-tip i { font-size: 48px; margin-bottom: 20px; color: #DCDFE6; }
</style>
